'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiChevronDown } from 'react-icons/fi';
import { LuRows3, LuColumns3 } from 'react-icons/lu';
import { BsToggleOn, BsToggleOff } from 'react-icons/bs';
import LeadsTable from './LeadsTable';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchLeads,
  setLeadsFilters,
  setLeadsPage,
  clearLeads,
  setLeadsFiltersNoPageReset,
} from '../../../redux/features/adminLeadSlice';
import { fetchAgents } from '../../../redux/features/agentSlice';
import TabbedList from '../../admin/Lead/ApplicationGrid';
import { forceLogout } from '../../../utlis/forceLogout';
import {
  useCountries,
  useVisaTypeFilters,
} from '../../client/dashboard/staticData';
import moment from 'moment';
import DateSelector from '../../admin/HomePage/DateSelector';
import { setGlobalCountries } from '../../../redux/features/countriesSlice';
import LimitDropdown from '../../common/LimitDropdown';
// import DateSelector from "../HomePage/DateSelector"

export default function MainLeads() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const value = location.state?.value;
  const stateCategory = location.state?.selectedCategory;
  const queryCategory = useMemo(
    () => new URLSearchParams(location.search).get('visaCategory'),
    [location.search],
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countries = useCountries();
  const visaTypeFilters = useVisaTypeFilters();
  const [user, setUser] = useState(null);
  const [isInitialSyncDone, setIsInitialSyncDone] = useState(false);
  const { leads, isLoading, error, totalPages, filters } = useSelector(
    (state) => state.leads,
  );
  const { agents } = useSelector((state) => state.agents);

  // Sync page from URL on mount and on navigation back
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam && !isNaN(pageParam)) {
      const page = parseInt(pageParam, 10);
      if (page !== filters.page) {
        dispatch(setLeadsPage(page));
      }
    } else if (filters.page !== 1) {
      // If no page param in URL, ensure page is 1
      dispatch(setLeadsPage(1));
    }
    setIsInitialSyncDone(true);

    return () => {
      // Don't clear leads to preserve state for back navigation
      // dispatch(clearLeads());
    };
  }, [searchParams]); // Listen to searchParams to detect back navigation

  // Sync page from Redux to URL
  useEffect(() => {
    if (!isInitialSyncDone) return;
    const currentUrlPage = parseInt(searchParams.get('page') || '1', 10);
    const reduxPage = filters.page || 1;

    if (currentUrlPage !== reduxPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', reduxPage.toString());
      setSearchParams(newParams);
    }
  }, [filters.page, setSearchParams, searchParams, isInitialSyncDone]);
    
  const globalCountries = useSelector(
    (state) => state.globalCountries.globalCountries,
  );

  useEffect(() => {
    // Check local storage for user data
    const userData = localStorage.getItem('user');
    if (!userData) {
      forceLogout();
    } else {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
  }, [navigate]);

  // Helper to map API status to UI status
  const mapApiToStatus = (apiStatus) => {
    switch (apiStatus) {
      case 'VisaApplied':
        return 'Visa Applied';
      case 'FCC (Re-open)':
        return 'FCC (Re-open)';
      default:
        return apiStatus || 'All';
    }
  };

  const getInitialDate = (dateStr, part) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (part === 'month') return date.getMonth() + 1;
    if (part === 'year') return date.getFullYear();
    return null;
  };

  const [isGridView, setIsGridView] = useState(false);
  const [searchInput, setSearchInput] = useState(filters?.searchTerm || '');
  const [searchTerm, setSearchTerm] = useState(filters?.searchTerm || '');
  const [selectedTab, setSelectedTab] = useState('All Applications');
  const [selectedCategory, setSelectedCategory] = useState(
    stateCategory || queryCategory || filters?.visaCategory || 'All',
  );
  const incomingCategory = stateCategory || queryCategory;
  const [selectedStatus, setSelectedStatus] = useState(
    mapApiToStatus(filters?.status),
  );
  const [selectedCountries, setSelectedCountries] = useState(
    filters?.countries?.length > 0 ? filters.countries : globalCountries || [],
  );
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [processTypes, setProcessTypes] = useState([]);
  const [selectedVisaExpiryDays, setSelectedVisaExpiryDays] = useState(
    filters?.visaExpiryDays || '',
  );
  const [selectedProcessType, setSelectedProcessType] = useState(
    filters?.processType || '',
  );
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState(filters?.agentIds || []);
  const agentDropdownRef = useRef(null);
  const visaExpiryDayOptions = useMemo(
    () => Array.from({ length: 365 }, (_, index) => index + 1),
    [],
  );
  const pageUrlSynced = useRef(false);
  const initialFiltersApplied = useRef(false);

  const isSubAdmin =
    user?.role === 'sub-admin' || user?.role === 'subadmin' || user?.role === 'admin';

  const activeAgents = useMemo(
    () => (agents || []).filter((agent) => agent.isActive),
    [agents],
  );

  const [fromMonth, setFromMonth] = useState(
    getInitialDate(filters?.fromDate, 'month'),
  );
  const [fromYear, setFromYear] = useState(
    getInitialDate(filters?.fromDate, 'year'),
  );
  const [toMonth, setToMonth] = useState(
    getInitialDate(filters?.toDate, 'month'),
  );
  const [toYear, setToYear] = useState(
    getInitialDate(filters?.toDate, 'year'),
  );

  useEffect(() => {
    const fetchProcessTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setProcessTypes([]);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/processtype`,
          {
            method: 'GET',
            headers: {
              authorization: `bearer ${token}`,
            },
          },
        );

        const data = await response.json();
        setProcessTypes(data?.body?.processTypes || []);
      } catch (error) {
        console.error('Error fetching process types:', error);
        setProcessTypes([]);
      }
    };

    fetchProcessTypes();
  }, []);

  useEffect(() => {
    if (isSubAdmin && agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [agents.length, dispatch, isSubAdmin]);

  // Initialize limit from filters or default
  const initialLeadsLimit = filters?.limit || 10
  const [leadsLimit, setLeadsLimit] = useState(initialLeadsLimit)
  useEffect(() => {
    dispatch(setLeadsFilters({ ...filters, limit: leadsLimit }))
    dispatch(setLeadsPage(1))
    // Refetch leads immediately after changing page/limit so UI updates
    dispatch(fetchLeads({}))
  }, [leadsLimit])

  const handleDateChange = (type, value) => {
    switch (type) {
      case 'fromMonth':
        setFromMonth(value);
        break;
      case 'fromYear':
        setFromYear(value);
        break;
      case 'toMonth':
        setToMonth(value);
        break;
      case 'toYear':
        setToYear(value);
        break;
      default:
        break;
    }
  };

  const handleCheckboxChange = (countryName) => {
    setSelectedCountries((prevSelected) => {
      const updatedSelection = prevSelected.includes(countryName)
        ? prevSelected.filter((name) => name !== countryName)
        : [...prevSelected, countryName];
      dispatch(setGlobalCountries(updatedSelection));
      return updatedSelection;
    });
  };

  useEffect(() => {
    const sameSelection =
      selectedCountries.length === globalCountries.length &&
      selectedCountries.every((country, index) => country === globalCountries[index]);

    if (sameSelection) return;

    setSelectedCountries(globalCountries);
    // Don't reset page here - let the filter effect handle pagination reset if needed
    // dispatch(setLeadsPage(1));
  }, [globalCountries, selectedCountries]);

  const handleAgentToggle = (agentId) => {
    setSelectedAgents((prev) =>
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId],
    );
  };

  const clearAgentSelection = () => setSelectedAgents([]);

  const CustomDateSelector = () => (
    <DateSelector
      className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto"
      fromMonth={fromMonth}
      setFromMonth={(value) => handleDateChange('fromMonth', value)}
      fromYear={fromYear}
      setFromYear={(value) => handleDateChange('fromYear', value)}
      toMonth={toMonth}
      setToMonth={(value) => handleDateChange('toMonth', value)}
      toYear={toYear}
      setToYear={(value) => handleDateChange('toYear', value)}
    />
  );

  useEffect(() => {
    // Only update local selectedCountries from globalCountries if we have no selection yet
    // and no filters are currently active (initial load scenario)
    if (
      globalCountries?.length > 0 &&
      selectedCountries.length === 0 &&
      (!filters.countries || filters.countries.length === 0)
    ) {
      console.log(
        'Syncing global countries to selection (initial load):',
        globalCountries,
      );
      setSelectedCountries(globalCountries);
    }
  }, [globalCountries, filters.countries, selectedCountries.length]);

  useEffect(() => {
    if (!isSubAdmin && selectedAgents.length > 0) {
      setSelectedAgents([]);
    }
  }, [isSubAdmin, selectedAgents.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isAgentDropdownOpen && agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setIsAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAgentDropdownOpen]);



  useEffect(() => {
    if (value === 'My Applications') {
      setSelectedTab(value);
    }
  }, [value]);

  useEffect(() => {
    if (incomingCategory && incomingCategory !== selectedCategory) {
      setSelectedCategory(incomingCategory);
    }
  }, [incomingCategory, selectedCategory]);

  const visaTypeOptions = useMemo(() => {
    const titles = (visaTypeFilters || [])
      .map((category) => category?.title)
      .filter(Boolean);

    if (!titles.includes('All')) {
      titles.unshift('All');
    }

    if (selectedCategory && !titles.includes(selectedCategory)) {
      titles.unshift(selectedCategory);
    }

    return [...new Set(titles)];
  }, [visaTypeFilters, selectedCategory]);

  const statusFilters = [
    'All',
    'Active/In Progress',
    'Pending',
    'Payment Pending',
    'Transaction Approval Pending',
    'Visa Applied',
    'FCC (Re-open)',
    'ART (Re-open)',
  ];

  // Compose dates for API only if dates are selected
  const fromDateStr = useMemo(() => {
    if (!fromYear || !fromMonth) return '';
    return `${fromYear}-${String(fromMonth).padStart(2, '0')}-01`;
  }, [fromYear, fromMonth]);
  const toDateStr = useMemo(() => {
    if (!toYear || !toMonth) return '';
    const endDay = moment(`${toYear}-${toMonth}`, 'YYYY-M').daysInMonth();
    return `${toYear}-${String(toMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
  }, [toYear, toMonth]);

  // Push filters handled below with proper API status mapping

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Map UI status to API status - send exact values to API
  const mapStatusToApi = (ui) => {
    switch (ui) {
      case 'All':
        return 'All';
      case 'Active/In Progress':
        return 'Active/In Progress';
      case 'Pending':
        return 'Pending';
      case 'Payment Pending':
        return 'Payment Pending';
      case 'Transaction Approval Pending':
        return 'Transaction Approval Pending';
      case 'Visa Applied':
        return 'VisaApplied';
      case 'FCC (Re-open)':
        return 'FCC (Re-open)';
      case 'ART (Re-open)':
        return 'ART (Re-open)';
      default:
        return ui; // Return as-is for any other status
    }
  };

  // Push filters to Redux (only include dates if selected)
  // useEffect(() => {
  //   const visa = selectedCategory;
  //   const apiStatus = mapStatusToApi(selectedStatus);
  //   dispatch(
  //     setLeadsFilters({
  //       fromDate: fromDateStr,
  //       toDate: toDateStr,
  //       countries: selectedCountries,
  //       visaCategory: visa,
  //       status: apiStatus,
  //       visaExpiryDays: selectedVisaExpiryDays,
  //       processType: selectedProcessType,
  //       searchTerm: searchTerm,
  //     }),
  //   );
  // }, [
  //   fromDateStr,
  //   toDateStr,
  //   selectedCountries,
  //   selectedCategory,
  //   selectedStatus,
  //   searchTerm,
  //   selectedVisaExpiryDays,
  //   selectedProcessType,
  // ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const visa = selectedCategory;
      const apiStatus = mapStatusToApi(selectedStatus);
      
      const filterPayload = {
        fromDate: fromDateStr,
        toDate: toDateStr,
        countries: selectedCountries,
        visaCategory: visa,
        status: apiStatus,
        visaExpiryDays: selectedVisaExpiryDays,
        processType: selectedProcessType,
        searchTerm: searchTerm,
        agentIds: isSubAdmin ? selectedAgents : [],
      };

      // During initial mount, don't reset page to preserve URL page param
      if (!initialFiltersApplied.current && !pageUrlSynced.current) {
        dispatch(setLeadsFiltersNoPageReset(filterPayload));
      } else if (!initialFiltersApplied.current) {
        // First time but page already synced from URL, preserve it
        dispatch(setLeadsFiltersNoPageReset(filterPayload));
      } else {
        // Subsequent changes by user, reset page to 1
        dispatch(setLeadsFilters(filterPayload));
      }

      if (!initialFiltersApplied.current) initialFiltersApplied.current = true;
    }, 600); // 500ms debounce

    return () => clearTimeout(handler); // clear previous timeout
  }, [
    fromDateStr,
    toDateStr,
    selectedCountries,
    selectedCategory,
    selectedStatus,
    searchTerm,
    selectedVisaExpiryDays,
    selectedProcessType,
    selectedAgents,
    isSubAdmin,
  ]);
  useEffect(() => {
    if (!isInitialSyncDone) return;
    // When arriving from dashboard with a category, avoid fetching with stale Redux filters.
    if (
      incomingCategory &&
      String(filters.visaCategory || 'All') !== String(selectedCategory || 'All')
    ) {
      return;
    }
    dispatch(fetchLeads({}));
  }, [
    dispatch,
    isInitialSyncDone,
    incomingCategory,
    selectedCategory,
    filters.fromDate,
    filters.toDate,
    filters.countries,
    filters.visaCategory,
    filters.status,
    filters.searchTerm,
    filters.page,
    filters.limit,
    filters.visaExpiryDays,
    filters.processType,
    filters.agentIds,
  ]);

  const handlePageChange = (newPage) => {
    const current = filters.page || 1;
    const max = totalPages || 1;
    const target = Math.min(Math.max(newPage, 1), max);
    if (target !== current) {
      dispatch(setLeadsPage(target));
    }
  };

  return (
    <div className="md:p-6 md:pt-3">
      {/* Header and Search Bar - Mobile Responsive */}
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-base font-normal md:text-xl lg:text-2xl lg:font-medium">
          {selectedTab}
        </h1>

       

        {/* Search bar row */}
        <div className="flex items-center w-full sm:w-[450px] justify-end mb-4">
          <div className="relative w-full max-w-xl">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or ID"
              className="w-full rounded-full border border-gray-200 py-2.5 pl-10 pr-4 focus:border-blue-500 focus:outline-none"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Country Dropdown - Mobile Only */}
      <div className="mb-4 flex md:hidden justify-start">
        <div className="relative">
          <button
            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            className="flex items-center space-x-2 rounded-full p-1.5 md:p-2 text-sm border border-[#87CA60]/60 bg-white drop-shadow-md text-black min-w-[200px] justify-between"
          >
            <div className="flex items-center space-x-2">
              {selectedCountries.length > 0 ? (
                <>
                  <div className="flex items-center space-x-1">
                    {selectedCountries.length <= 8 &&
                      selectedCountries.map((name) => {
                        const country = countries.find((c) => c.name === name);
                        return country ? (
                          <img
                            key={country.name}
                            src={country.flag || '/placeholder.svg'}
                            alt={country.name}
                            className="w-6 h-6 rounded-full object-cover border border-slate-300"
                          />
                        ) : null;
                      })}
                  </div>
                  {selectedCountries.length > 8 && (
                    <span className="text-sm text-gray-600">
                      +{selectedCountries.length - 8} more
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">Select countries</span>
              )}
            </div>

            <svg
              className={`w-4 h-4 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isCountryDropdownOpen && (
            <div className="absolute left-0 mt-2 w-[250px] bg-white border rounded-lg shadow-lg py-1 z-10 max-h-60 overflow-y-auto">
              {countries.map((country) => (
                <label
                  key={country.name}
                  className="w-full flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 space-x-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedCountries.includes(country.name)}
                    onChange={() => handleCheckboxChange(country.name)}
                    className="cursor-pointer"
                  />
                  <img
                    src={country.flag || '/placeholder.svg'}
                    alt={country.name}
                    loading="lazy"
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="text-black">{country.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs Section */}
      <div className="mb-3 md:mb-6 flex items-center gap-6 text-lg font-medium">
        {/* Tabs for larger screens */}
        <div className="hidden md:flex gap-6">
          <span
            className={`cursor-pointer ${selectedTab === 'All Applications' ? 'text-black' : 'text-gray-400'}`}
            onClick={() => setSelectedTab('All Applications')}
          >
            All Applications
          </span>

          <span
            className={`cursor-pointer ${selectedTab === 'My Applications' ? 'text-black' : 'text-gray-400'}`}
            onClick={() => setSelectedTab('My Applications')}
          >
            My Applications
          </span>
        </div>

        {/* Dropdown for small screens */}
        <div className="md:hidden w-full">
          <select
            value={selectedTab}
            onChange={(e) => setSelectedTab(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#005787]"
          >
            <option value="All Applications">All Applications</option>
            <option value="My Applications">My Applications</option>
          </select>
        </div>
      </div>

      {/* Unified Responsive Filters Section */}
      <div className="w-full mb-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
          {/* Left group: filter controls */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:flex xl:flex-nowrap xl:flex-1 xl:items-end xl:gap-2">
            {/* Visa Type */}
            <div className="relative w-full xl:w-[150px] xl:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                title={selectedCategory}
              >
                {visaTypeOptions.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            {/* Application Status */}
            <div className="relative w-full xl:w-[155px] xl:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                title={selectedStatus}
              >
                {statusFilters.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            {/* Visa Expiry Days */}
                <div className="relative w-full xl:w-[130px] xl:flex-none">
                <input
                type="number"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#005787] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                value={selectedVisaExpiryDays}
                onKeyDown={(e) => {
                  // Prevent typing minus, plus, decimal and exponential chars
                  if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData('text') || '';
                  const digits = pasted.replace(/\D+/g, '');
                  if (!digits) return;
                  const num = parseInt(digits, 10);
                  const clamped = Math.min(Math.max(num, 1), 365);
                  setSelectedVisaExpiryDays(clamped);
                }}
                onChange={(e) => {
                  const raw = String(e.target.value || '').replace(/\D+/g, '');
                  if (raw === '') {
                    setSelectedVisaExpiryDays('');
                    return;
                  }
                  const num = parseInt(raw, 10);
                  if (Number.isNaN(num)) return;
                  if (num < 1) setSelectedVisaExpiryDays(1);
                  else if (num > 365) setSelectedVisaExpiryDays(365);
                  else setSelectedVisaExpiryDays(num);
                }}
                placeholder="Visa expiry"
                min={1}
                max={365}
              />
            </div>

            {/* Process Type */}
            <div className="relative w-full xl:w-[140px] xl:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedProcessType}
                onChange={(e) => setSelectedProcessType(e.target.value)}
                title={selectedProcessType}
              >
                <option value="">Process Type</option>
                {processTypes.map((pt) => (
                  <option key={pt._id} value={pt._id} className='whitespace-normal break-words'>
                    {pt.name}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            {/* Agents */}
            {isSubAdmin && (
              <div ref={agentDropdownRef} className="relative w-full xl:w-[140px] xl:flex-none">
                <button
                  type="button"
                  onClick={() => setIsAgentDropdownOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between rounded border border-gray-300 bg-white py-2 pl-3 pr-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                >
                  <span className="truncate">
                    {selectedAgents.length > 0
                      ? `${selectedAgents.length} selected`
                      : 'Agents'}
                  </span>
                  <FiChevronDown
                    className={`ml-1 shrink-0 text-gray-500 transition-transform ${
                      isAgentDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isAgentDropdownOpen && (
                  <div className="absolute left-0 top-full z-30 mt-1 w-48 rounded border border-gray-200 bg-white shadow-lg">
                    <div className="max-h-56 overflow-y-auto p-1">
                      {activeAgents.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-400">
                          No agents found
                        </p>
                      ) : (
                        activeAgents.map((agent) => (
                          <label
                            key={agent._id}
                            className="flex cursor-pointer items-center gap-2 rounded px-3 py-1.5 text-sm hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[#005787]"
                              checked={selectedAgents.includes(agent._id)}
                              onChange={() => handleAgentToggle(agent._id)}
                            />
                            <span className="truncate">
                              {agent.firstName} {agent.lastName}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    {selectedAgents.length > 0 && (
                      <div className="border-t border-gray-100 px-3 py-1.5">
                        <button
                          onClick={clearAgentSelection}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Clear selection
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right group: From / To dates */}
          <div className="flex justify-end xl:justify-start xl:flex-none xl:min-w-[260px]">
            <div className="w-full md:max-w-[400px] xl:max-w-none xl:w-auto">
              <CustomDateSelector />
            </div>
          </div>
        </div>
      </div>

      {/* Toggle Buttons */}

      {/* Render Applications */}
      {(() => {
        const rows = Array.isArray(leads) ? leads : [];

        // Sort by date (latest first) - handle locally if needed
        // const rows = [...rows].sort((a, b) => {
        //   const dateA = new Date(a.createdAt || a.updatedAt || 0);
        //   const dateB = new Date(b.createdAt || b.updatedAt || 0);
        //   return dateB - dateA; // Descending order (latest first)
        // });

        return isGridView ? (
          <TabbedList filteredApplications={rows} />
        ) : (
          <LeadsTable filteredApplications={rows} />
        );
      })()}

      {/* Pagination (match ApplicationLog UI) */}
        <div className="flex items-center justify-center gap-2 mt-6">
      {totalPages > 1 && (
<>
          <button
            onClick={() =>
              handlePageChange(Math.max((filters.page || 1) - 1, 1))
            }
            disabled={(filters.page || 1) === 1 || isLoading}
            className={`px-2 md:px-3 py-2 rounded-md ${
              (filters.page || 1) === 1 || isLoading
                ? 'bg-gray-200/50 text-gray-400 cursor-default'
                : 'bg-gray-100 text-[#005787] hover:bg-gray-300'
            }`}
          >
            &lt;
          </button>

          {/* Page Numbers with ellipses */}
          {Array.from({ length: totalPages }).map((_, index) => {
            const pageNumber = index + 1;
            const current = filters.page || 1;
            if (
              pageNumber === 1 ||
              pageNumber === totalPages ||
              (pageNumber >= current - 1 && pageNumber <= current + 1)
            ) {
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md border ${
                    current === pageNumber
                      ? 'bg-[#005787] text-white border-[#334155]'
                      : 'bg-gray-100 text-[#005787] hover:bg-gray-300 border-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            }
            if (pageNumber === current - 2 || pageNumber === current + 2) {
              return (
                <span key={pageNumber} className="px-2 py-1 text-gray-500">
                  ...
                </span>
              );
            }
            return null;
          })}

          {/* Next Button */}
          <button
            onClick={() =>
              handlePageChange(
                Math.min((filters.page || 1) + 1, totalPages || 1),
              )
            }
            disabled={(filters.page || 1) === (totalPages || 1) || isLoading}
            className={`px-2 md:px-3 py-2 rounded-md ${
              (filters.page || 1) === (totalPages || 1) || isLoading
                ? 'bg-gray-200/50 text-gray-400 cursor-default'
                : 'bg-gray-100 text-[#005787] hover:bg-gray-300'
            }`}
          >
            &gt;
          </button>
          </>
      )}
      <div className="hidden md:flex items-center gap-3">
        <LimitDropdown value={leadsLimit} onChange={(v) => { setLeadsLimit(v); }} />
              </div>
        </div>
    </div>
  );
}
